import React, {useEffect, useState} from "react";
import axios from "axios";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, getLoggedInUserId, showToast, generateRandomNumber, registeredUpdatedTitle} from "../../utils/utility";
import SlideOver from "../../components/ui/SlideOver";
import TextField from "../../components/ui/TextField";
import Select from "../../components/ui/Select";
import Button from "../../components/ui/Button";
import {ROLE_OPTIONS, ASSIGNABLE_ROLE_OPTIONS} from "./userRoles";
import {COUNTRIES} from "../../component/shared/CountrySelector";

const EMPTY_FORM = {
  fullName: "",
  username: "",
  email: "",
  phoneNumber: "",
  countryIso: "IN", // drives isoCountryCode + countryCode (dialling) together
  userRole: "",
  streetAddress: "",
  city: "",
  state: "",
  postalCode: "",
  idType: "",
  idNumber: "",
};

const countryByIso = (iso) => COUNTRIES.find((c) => c.isoCode === iso) || null;

/**
 * Unified slide-over for creating + editing a user.
 *
 *   mode  : "create" | "edit"
 *   user  : (edit mode) the user object
 *   open  : boolean
 *   onClose, onSaved(updatedUser)
 *
 * Posts to /editUser/{userId} for edits, /registerUser-style endpoint for creates.
 * The full registration form (with address, ID type, etc.) still lives in the
 * /users → Register route — this slide-over is the quick-add path that captures
 * the minimum required fields. Backend defaults apply to the rest.
 */
export default function UserSlideOver({mode = "edit", user, open, onClose, onSaved}) {
  const [form, setForm] = useState({...EMPTY_FORM});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && user) {
        setForm({
          fullName: user.fullName || "",
          username: user.username || "",
          email: user.email || "",
          phoneNumber: user.phoneNumber || "",
          countryIso: user.isoCountryCode || "IN",
          userRole: user.userRole || "",
          streetAddress: user.streetAddress || "",
          city: user.city || "",
          state: user.state || "",
          postalCode: user.postalCode || "",
          idType: user.idType || "",
          idNumber: user.idNumber || "",
        });
      } else {
        setForm({...EMPTY_FORM});
      }
      setErrors({});
    }
  }, [open, mode, user]);

  const change = (k, v) => {
    setForm((prev) => ({...prev, [k]: v}));
    if (errors[k]) setErrors((prev) => ({...prev, [k]: undefined}));
  };

  const validate = () => {
    const next = {};
    const email = form.email.trim();
    const phone = form.phoneNumber.trim();
    if (!form.fullName.trim()) next.fullName = "Full name is required.";
    if (!form.userRole) next.userRole = "Pick a role.";
    // Messaging contract: at least ONE of email / phone (welcome credentials
    // go out on these channels), and country is mandatory — it supplies the
    // WhatsApp dialling code and the ISO country code.
    if (mode === "create" && !email && !phone) {
      next.email = "Enter an email or a phone number.";
      next.phoneNumber = "Enter an email or a phone number.";
    }
    if (!form.countryIso) next.countryIso = "Pick a country.";
    if (email && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email))
      next.email = "Enter a valid email (e.g. user@company.com).";
    if (phone && !/^\+?\d{7,15}$/.test(phone.replace(/[\s-]/g, "")))
      next.phoneNumber = "Enter a valid phone number (digits only).";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      if (mode === "edit") {
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/editUser/${user.userId}`;
        const country = countryByIso(form.countryIso);
        const payload = {
          ...form,
          isoCountryCode: country?.isoCode || null,
          countryCode: country?.dialingCode || null,
        };
        await axios.put(url, payload);
        showToast("User updated", {type: "success"});
        onSaved && onSaved({...user, ...payload});
      } else {
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlConstants.USERS}`;
        const requestBody = {
          requestId: generateRandomNumber().toString(),
          clientId: getClientIdOfUser(),
          userId: getLoggedInUserId(),
          requestBody: {
            ...form,
            email: form.email.trim() || null,
            phoneNumber: form.phoneNumber.trim() || null,
            // Country drives both messaging fields: dialling code (WhatsApp)
            // and ISO code (NOT NULL in production).
            isoCountryCode: countryByIso(form.countryIso)?.isoCode || null,
            countryCode: countryByIso(form.countryIso)?.dialingCode || null,
            // Blank username lets the backend derive one: email when present,
            // otherwise name + last-4-digits (e.g. ramesh3388).
            username: form.username.trim() || form.email.trim() || null,
            userStatus: "ACTIVE",
          },
        };
        const res = await axios.post(url, requestBody);
        const created = res?.data?.responseData || null;
        if (created?.generatedPassword) {
          // One-time temporary password — shown only here, never again.
          showToast(
            `User added. Temporary password: ${created.generatedPassword} — share it securely; it won't be shown again.`,
            {type: "info", autoClose: false}
          );
        } else {
          showToast("User added", {type: "success"});
        }
        onSaved && onSaved(created);
      }
      onClose && onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Could not save user.";
      showToast(typeof msg === "string" ? msg : "Could not save user.", {type: "error"});
    } finally {
      setSaving(false);
    }
  };

  // MOBI_TRACK (Track Point) isn't offered here — registerUser/editUser have no asset-linking
  // logic, so assigning it through this slide-over would create a login with no linked
  // ClientAsset (invisible on the Track Point dashboard/device list/live tracking). New Track
  // Point drivers go through Assets -> Register Asset -> MOBI_TRACK instead. An existing Track
  // Point user keeps their role visible when editing their other fields, rather than being
  // forced off it by a picker that can't put it back.
  const roleOptions =
    mode === "edit" && user?.userRole === "MOBI_TRACK" ? ROLE_OPTIONS : ASSIGNABLE_ROLE_OPTIONS;

  const title = mode === "edit" ? "Edit user" : "Add user";
  const subtitle = mode === "edit" && user ? `${user.fullName || user.username} · ID ${user.userId}` : "Quick-add a new team member.";

  return (
    <SlideOver open={open} onOpenChange={(o) => !o && onClose && onClose()} title={title} subtitle={subtitle} size="md">
      <SlideOver.Body>
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label="Full name"
              required
              value={form.fullName}
              onChange={(e) => change("fullName", e.target.value)}
              error={errors.fullName}
              placeholder="Sarvesh B"
              autoFocus={mode === "create"}
            />
            <div className="space-y-1">
              <label className="block text-sm font-medium text-ink-700">
                Role <span className="text-danger-600">*</span>
              </label>
              <Select
                value={form.userRole}
                onChange={(e) => change("userRole", e.target.value)}
                hasError={!!errors.userRole}
              >
                <option value="">Select role…</option>
                {roleOptions.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </Select>
              {errors.userRole && <p className="text-xs text-danger-600">{errors.userRole}</p>}
            </div>
            {mode === "create" && (
              <TextField
                label="Username"
                value={form.username}
                onChange={(e) => change("username", e.target.value.replace(/\s+/g, ""))}
                placeholder="Leave blank to auto-generate"
                helper="Auto: email, or name + last 4 phone digits (ramesh3388)."
                autoComplete="off"
              />
            )}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-ink-700">
                Country <span className="text-danger-600">*</span>
              </label>
              <Select
                value={form.countryIso}
                onChange={(e) => change("countryIso", e.target.value)}
                hasError={!!errors.countryIso}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.isoCode} value={c.isoCode}>
                    {c.flag} {c.name} ({c.dialingCode})
                  </option>
                ))}
              </Select>
              {errors.countryIso
                ? <p className="text-xs text-danger-600">{errors.countryIso}</p>
                : <p className="text-xs text-ink-500">Sets the WhatsApp dialling code for the phone number.</p>}
            </div>
            <TextField
              label="Email"
              required={mode === "create" && !form.phoneNumber.trim()}
              type="email"
              value={form.email}
              onChange={(e) => change("email", e.target.value.replace(/\s+/g, ""))}
              error={errors.email}
              placeholder="user@company.com"
              autoComplete="email"
              disabled={mode === "edit"}
              helper={mode === "edit" ? "Email cannot be changed after registration." : "Email or phone — at least one is required."}
            />
            <TextField
              label="Phone"
              required={mode === "create" && !form.email.trim()}
              value={form.phoneNumber}
              onChange={(e) => change("phoneNumber", e.target.value)}
              error={errors.phoneNumber}
              placeholder="9876543210"
              type="tel"
              autoComplete="tel"
              helper={mode === "create" ? "Phone-only users get a short username like ramesh3388 — or just sign in with the phone number." : undefined}
            />
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-ink-500 mb-2">Address (optional)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-3">
                <TextField label="Street address" value={form.streetAddress} onChange={(e) => change("streetAddress", e.target.value)} placeholder="221B MG Road"/>
              </div>
              <TextField label="City" value={form.city} onChange={(e) => change("city", e.target.value)} placeholder="Bengaluru"/>
              <TextField label="State" value={form.state} onChange={(e) => change("state", e.target.value)} placeholder="Karnataka"/>
              <TextField label="Postal code" value={form.postalCode} onChange={(e) => change("postalCode", e.target.value)} placeholder="560001"/>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-ink-500 mb-2">Identification (optional)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextField label="ID type" value={form.idType} onChange={(e) => change("idType", e.target.value)} placeholder="License / Passport / Aadhaar"/>
              <TextField label="ID number" value={form.idNumber} onChange={(e) => change("idNumber", e.target.value)} placeholder="ID #12345"/>
            </div>
          </div>

          {mode === "create" && (
            <div className="rounded-lg bg-info-50 border border-info-100 px-4 py-3 text-sm text-info-700">
              Sign-in details are sent to the user's email and WhatsApp
              automatically. A temporary password is also shown here once after
              creation as a fallback. Address and other details are optional and
              can be completed from the user profile later.
            </div>
          )}

          {mode === "edit" && user && (
            <p className="text-xs text-ink-500 pt-3 border-t border-ink-100">
              {registeredUpdatedTitle(user.uploadDate, user.updatedAt)}
            </p>
          )}
        </div>
      </SlideOver.Body>
      <SlideOver.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={saving}>
          {mode === "edit" ? "Save changes" : "Add user"}
        </Button>
      </SlideOver.Footer>
    </SlideOver>
  );
}
